import React, { useState, createContext, useContext } from 'react';
type Language = 'fr' | 'en';
type Translations = {
  hero: {
    title: string;
    subtitle: string;
    ctaPrimary: string;
    ctaSecondary: string;
    systemStatus: string;
    scroll: string;
  };
  services: {
    title: string;
    subtitle: string;
    items: {
      webAi: {
        title: string;
        desc: string;
      };
      cloud: {
        title: string;
        desc: string;
      };
      business: {
        title: string;
        desc: string;
      };
      wifi: {
        title: string;
        desc: string;
      };
      managed: {
        title: string;
        desc: string;
      };
    };
  };
  pricing: {
    title: string;
    subtitle: string;
    cards: {
      website: {
        title: string;
        price: string;
        priceSubtext: string;
        description: string;
        features: string[];
        cta: string;
      };
      application: {
        title: string;
        price: string;
        priceSubtext: string;
        description: string;
        features: string[];
        cta: string;
      };
      consulting: {
        title: string;
        price: string;
        priceSubtext: string;
        description: string;
        features: string[];
        cta: string;
      };
    };
  };
  feature: {
    badge: string;
    title: string;
    subtitle: string;
    features: string[];
    aiStatus: string;
  };
  methodology: {
    title: string;
    steps: {
      discovery: {
        title: string;
        desc: string;
      };
      strategy: {
        title: string;
        desc: string;
      };
      implementation: {
        title: string;
        desc: string;
      };
      support: {
        title: string;
        desc: string;
      };
    };
  };
  tech: {
    title: string;
    subtitle: string;
    pillars: {
      cloud: string;
      ai: string;
      performance: string;
      hosting: string;
    };
  };
  stats: {
    uptime: string;
    clients: string;
    response: string;
    satisfaction: string;
  };
  cta: {
    title: string;
    subtitle: string;
    primary: string;
    secondary: string;
  };
  contact: {
    title: string;
    cta: string;
  };
  footer: {
    copyright: string;
    privacy: string;
    status: string;
  };
};
const translations: Record<Language, Translations> = {
  fr: {
    hero: {
      title: 'Sites Web IA Nouvelle Génération',
      subtitle: "Création, hébergement et maintenance de sites web intelligents propulsés par l'IA. Basés en Nouvelle-Zélande, nous accompagnons les entreprises dans leur transformation digitale.",
      ctaPrimary: 'Démarrer votre projet',
      ctaSecondary: 'Découvrir nos solutions',
      systemStatus: 'STATUT SYSTÈME : EN LIGNE',
      scroll: 'Défiler'
    },
    services: {
      title: "Nos Domaines d'Expertise",
      subtitle: 'Solutions de sécurité complètes pour le web décentralisé',
      items: {
        webAi: {
          title: 'Sites Web IA',
          desc: "Création de sites web intelligents intégrant l'intelligence artificielle pour une expérience utilisateur optimale"
        },
        cloud: {
          title: 'Cloud Computing',
          desc: 'Infrastructure cloud professionnelle sur AWS, Azure et GCP pour une scalabilité maximale'
        },
        business: {
          title: 'Solutions Business',
          desc: 'Outils collaboratifs et solutions métiers adaptés à vos besoins spécifiques'
        },
        wifi: {
          title: 'Wi-Fi Professionnel',
          desc: 'Déploiement de réseaux sans fil professionnels haute performance et sécurisés'
        },
        managed: {
          title: 'Infogérance & Support',
          desc: 'Accompagnement digital complet, maintenance évolutive et support IT dédié'
        }
      }
    },
    pricing: {
      title: 'Tarifs Transparents',
      subtitle: 'Solutions adaptées à chaque besoin',
      cards: {
        website: {
          title: 'Création Site Web IA',
          price: '€500',
          priceSubtext: '≈ NZ$1,015',
          description: "Site web propulsé par l'IA, adapté aux besoins de votre entreprise.",
          features: ['Design responsive & moderne', 'Contenu & structure assistés par IA', 'Base SEO optimisée', 'Évolutivité facilitée'],
          cta: 'Commencer'
        },
        application: {
          title: 'Développement Application IA',
          price: 'Sur devis',
          priceSubtext: '',
          description: 'Applications IA personnalisées conçues pour la scalabilité et la performance.',
          features: ['Applications web ou mobile', 'Intégrations IA & automatisation', 'Architecture sécurisée', 'Fonctionnalités sur mesure'],
          cta: 'Demander un devis'
        },
        consulting: {
          title: 'Conseil Réseau & Systèmes',
          price: '€150/h',
          priceSubtext: '≈ NZ$305/h',
          description: 'Conseil expert pour réseau, cloud et infrastructure système.',
          features: ['Design & optimisation infrastructure', 'Audit sécurité & fiabilité', 'Conseil cloud & on-premise', 'Guidance IT stratégique'],
          cta: 'Réserver une consultation'
        }
      }
    },
    feature: {
      badge: 'Technologie de Pointe',
      title: 'Intelligence Artificielle Intégrée',
      subtitle: "Notre expertise en IA transforme vos sites web en plateformes intelligentes et évolutives. De la conception à l'hébergement, nous gérons l'intégralité de votre présence digitale.",
      features: ['Intégration IA Native', 'Hébergement Cloud Optimisé', 'Maintenance Proactive', 'Évolution Continue'],
      aiStatus: 'IA : ACTIVE'
    },
    methodology: {
      title: 'Notre Approche',
      steps: {
        discovery: {
          title: 'Analyse',
          desc: 'Étude approfondie de vos objectifs et besoins métiers'
        },
        strategy: {
          title: 'Conception',
          desc: 'Architecture technique et stratégie digitale sur mesure'
        },
        implementation: {
          title: 'Développement',
          desc: 'Création et intégration IA avec méthodologie agile'
        },
        support: {
          title: 'Accompagnement',
          desc: 'Hébergement, maintenance et évolution continue'
        }
      }
    },
    tech: {
      title: 'Excellence Technologique',
      subtitle: "Nous combinons les technologies cloud de pointe avec l'intelligence artificielle pour créer des solutions web performantes, sécurisées et évolutives.",
      pillars: {
        cloud: 'Infrastructure Cloud',
        ai: 'Intelligence Artificielle',
        performance: 'Haute Performance',
        hosting: 'Hébergement Premium'
      }
    },
    stats: {
      uptime: 'Disponibilité',
      clients: 'Projets Réalisés',
      response: 'Temps de Réponse',
      satisfaction: 'Satisfaction Client'
    },
    cta: {
      title: 'Prêt à Propulser Votre Présence Digitale ?',
      subtitle: 'Rejoignez les entreprises qui font confiance à Tuiit pour leur transformation digitale. De la Nouvelle-Zélande au monde entier.',
      primary: 'Démarrer Mon Projet',
      secondary: 'Nous Contacter'
    },
    contact: {
      title: 'Transformons ensemble votre vision digitale',
      cta: 'Discutons de votre projet'
    },
    footer: {
      copyright: '© 2026 TUIIT // TOUS DROITS RÉSERVÉS',
      privacy: 'PROTOCOLE DE CONFIDENTIALITÉ',
      status: 'STATUT SYSTÈME'
    }
  },
  en: {
    hero: {
      title: 'Next-Gen AI-Powered Websites',
      subtitle: 'Creation, hosting, and maintenance of intelligent AI-driven websites. Based in New Zealand, we support businesses in their digital transformation journey.',
      ctaPrimary: 'Start Your Project',
      ctaSecondary: 'Explore Solutions',
      systemStatus: 'SYSTEM STATUS: ONLINE',
      scroll: 'Scroll'
    },
    services: {
      title: 'Our Areas of Expertise',
      subtitle: 'Comprehensive security solutions for the decentralized web',
      items: {
        webAi: {
          title: 'AI Websites',
          desc: 'Creation of intelligent websites integrating artificial intelligence for optimal user experience'
        },
        cloud: {
          title: 'Cloud Computing',
          desc: 'Professional cloud infrastructure on AWS, Azure, and GCP for maximum scalability'
        },
        business: {
          title: 'Business Solutions',
          desc: 'Collaborative tools and business solutions tailored to your specific needs'
        },
        wifi: {
          title: 'Professional Wi-Fi',
          desc: 'Deployment of high-performance, secure professional wireless networks'
        },
        managed: {
          title: 'Managed Services',
          desc: 'Complete digital support, ongoing maintenance, and dedicated IT assistance'
        }
      }
    },
    pricing: {
      title: 'Transparent Pricing',
      subtitle: 'Solutions tailored to every need',
      cards: {
        website: {
          title: 'AI Website Creation',
          price: 'NZ$1,015',
          priceSubtext: '≈ €500',
          description: 'AI-powered website creation tailored to your business needs.',
          features: ['Responsive & modern design', 'AI-assisted content & structure', 'SEO-ready foundation', 'Easy future scalability'],
          cta: 'Get Started'
        },
        application: {
          title: 'AI Application Development',
          price: 'Contact us for a quote',
          priceSubtext: '',
          description: 'Custom AI applications designed for scalability and performance.',
          features: ['Web or mobile applications', 'AI integrations & automation', 'Secure architecture', 'Custom features on demand'],
          cta: 'Request a Quote'
        },
        consulting: {
          title: 'Network & Systems Consulting',
          price: 'NZ$305/h',
          priceSubtext: '≈ €150/h',
          description: 'Expert consulting for network, cloud, and system infrastructure.',
          features: ['Infrastructure design & optimization', 'Security & reliability review', 'Cloud & on-prem advisory', 'Strategic IT guidance'],
          cta: 'Book a Consultation'
        }
      }
    },
    feature: {
      badge: 'Cutting-Edge Technology',
      title: 'Integrated Artificial Intelligence',
      subtitle: 'Our AI expertise transforms your websites into intelligent, scalable platforms. From design to hosting, we manage your entire digital presence.',
      features: ['Native AI Integration', 'Optimized Cloud Hosting', 'Proactive Maintenance', 'Continuous Evolution'],
      aiStatus: 'AI: ACTIVE'
    },
    methodology: {
      title: 'Our Approach',
      steps: {
        discovery: {
          title: 'Analysis',
          desc: 'In-depth study of your objectives and business needs'
        },
        strategy: {
          title: 'Design',
          desc: 'Technical architecture and tailored digital strategy'
        },
        implementation: {
          title: 'Development',
          desc: 'AI creation and integration with agile methodology'
        },
        support: {
          title: 'Support',
          desc: 'Hosting, maintenance, and continuous evolution'
        }
      }
    },
    tech: {
      title: 'Technological Excellence',
      subtitle: 'We combine cutting-edge cloud technologies with artificial intelligence to create high-performance, secure, and scalable web solutions.',
      pillars: {
        cloud: 'Cloud Infrastructure',
        ai: 'Artificial Intelligence',
        performance: 'High Performance',
        hosting: 'Premium Hosting'
      }
    },
    stats: {
      uptime: 'Uptime',
      clients: 'Projects Delivered',
      response: 'Response Time',
      satisfaction: 'Client Satisfaction'
    },
    cta: {
      title: 'Ready to Elevate Your Digital Presence?',
      subtitle: 'Join the businesses that trust Tuiit for their digital transformation. From New Zealand to the world.',
      primary: 'Start My Project',
      secondary: 'Contact Us'
    },
    contact: {
      title: "Let's transform your digital vision together",
      cta: "Let's discuss your project"
    },
    footer: {
      copyright: '© 2026 TUIIT // ALL RIGHTS RESERVED',
      privacy: 'PRIVACY PROTOCOL',
      status: 'SYSTEM STATUS'
    }
  }
};
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [language, setLanguage] = useState<Language>('en');
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t: translations[language]
  }} data-id="element-386">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}